import { useEffect, useRef, useState } from 'react'
import { getSocket } from '../socket'

interface EditState { resource: string, status: 'granted' | 'queued', position: number }

export interface EditLock {
    canEdit: boolean
    status: 'granted' | 'queued'
    position: number
    // true once the lock was handed over after waiting (for a "you can edit now" hint)
    justGranted: boolean
}

// Acquire a collaborative edit lock for `resource` ('<type>:<id>') while the
// component (an edit-form tab) is mounted; release on unmount (tab close). Pass
// null for new records (no lock needed). See server contracts/editlock.ts.
export function useEditLock(resource: string | null): EditLock {
    const [state, setState] = useState<EditState>({ resource: resource ?? '', status: 'granted', position: 0 })
    const wasQueued = useRef(false)

    useEffect(() => {
        if (!resource) {
            setState({ resource: '', status: 'granted', position: 0 })
            return
        }
        const s = getSocket()
        const onState = (st: EditState) => {
            if (st.resource !== resource) return
            if (st.status === 'queued') wasQueued.current = true
            setState(st)
        }
        s.on('edit:state', onState)
        s.emit('edit:acquire', { resource })
        return () => {
            s.off('edit:state', onState)
            s.emit('edit:release', { resource })
        }
    }, [resource])

    const canEdit = state.status === 'granted'
    return { canEdit, status: state.status, position: state.position, justGranted: canEdit && wasQueued.current }
}
